import React from 'react';
import { Box, Typography, Grid, Chip, CircularProgress } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useVacancyMaintenance } from '../../hooks/useVacancyMaintenance';
import VacancyHouseSelectionDropDown from './components/vacancy-house-selection-dropdown';
import { VacancyStatusDropDown } from './components/vacancy-status-dropdown';
import { TablePagination } from '@/components/design-system/molecules/table/components/table-pagination';
import { VacancyMaintenanceFiltersProvider } from '../../hooks/use-vacancy-maintenance-filters';
import { EmptyState } from '@/components/design-system/atoms/empty-state';

const StatusBadge = ({
  backgroundColor,
  count,
  label,
}: {
  backgroundColor: string;
  count: number;
  label: string;
}) => (
  <div
    className="flex h-4 min-w-[20px] items-center justify-center gap-1 rounded-lg px-2 py-[1.2rem] text-footnote2 backdrop-blur-[8px]"
    style={{ backgroundColor }}
  >
    <Typography
      className="vacancy-maintenance-overview font-medium text-[#212529]"
      sx={{
        '&.pdf-export': {
          marginTop: '-10px',
        },
      }}
    >
      Total {count} {label}
    </Typography>
  </div>
);

const VacancyMaintenanceContent: React.FC = () => {
  const { t } = useTranslation();
  const { data, loading, error, page, pageLimit, setPage, setPageLimit } = useVacancyMaintenance();

  if (loading || !data) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height={400}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height={400}>
        <Typography color="error">{error?.message}</Typography>
      </Box>
    );
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'maintenance':
        return {
          background: '#FFF6E9',
          color: '#B07315',
          border: '#FCA41E',
        };
      case 'vacant':
        return {
          background: '#E8F5F3',
          color: '#1B998B',
          border: '#1B998B',
        };
      default:
        return {
          background: '#ECF0F4',
          color: '#3C6895',
          border: '#AFC1D4',
        };
    }
  };

  const getStatusChip = (status: string, since: string) => {
    const colors = getStatusColor(status);

    return (
      <div className="flex flex-col items-end gap-1">
        <div
          className="inline-flex items-center rounded-full px-2 py-1"
          style={{
            background: colors.background,
            border: `1px solid ${colors.border}`,
          }}
        >
          <Typography
            className="vacancy-maintenance-overview whitespace-nowrap font-sans text-body font-medium"
            sx={{
              '&.pdf-export': {
                marginTop: '-8px',
                paddingBottom: '4px',
              },
            }}
            style={{ color: colors.color }}
          >
            {t(`js.features.admin.stats.dashboard.vacancy.${status}`)}
          </Typography>
        </div>
        <Typography
          variant="caption"
          className="whitespace-nowrap font-sans text-footnote1 font-medium text-N700"
        >
          {since && `${t('js.features.admin.stats.dashboard.vacancy.since')} ${since}`}
        </Typography>
      </div>
    );
  };

  return (
    <Box className="flex h-full flex-col rounded-[16px] bg-white p-4 shadow-small">
      <Typography className="mb-3 font-Vollkorn text-lead1 font-bold leading-[27px] text-[#111827]">
        {t('js.features.admin.stats.dashboard.vacancy.title')}
      </Typography>

      <div className="mb-2 flex gap-1">
        <StatusBadge
          backgroundColor="#FDCA7DCC"
          count={data?.data?.total_maintenance ?? 0}
          label="in Maintenance"
        />
        <StatusBadge
          backgroundColor="#7BC4BCCC"
          count={data?.data?.total_vacant ?? 0}
          label="Vacant"
        />
      </div>

      <Box
        className="vacancy-maintenance-overview mb-4 flex w-full gap-4"
        sx={{
          '&.pdf-export': {
            display: 'none !important',
          },
        }}
      >
        <div>
          <VacancyHouseSelectionDropDown />
        </div>
        <div>
          <VacancyStatusDropDown />
        </div>
      </Box>

      <Box sx={{ maxHeight: 570, overflow: 'auto', flex: 1 }}>
        {data.data.rooms?.length > 0 ? (
          data.data.rooms.map((room) => (
            <Box
              key={room.id}
              className="mb-2 flex h-10 items-center justify-between rounded-[8px] bg-N100 p-4"
              onClick={() => window.open(`/admin/colocations/${room.house_id}#rooms`, '_new')}
              style={{ cursor: 'pointer' }}
            >
              <Box>
                <Typography
                  variant="subtitle1"
                  className="font-sans text-[14px] font-bold text-N900"
                >
                  {room.name}
                </Typography>
                <Typography
                  variant="body2"
                  className="pb-1 font-sans text-[14px] font-bold text-N900"
                >
                  €{room.rent}/month
                </Typography>
                <Typography
                  variant="body2"
                  className="font-sans text-footnote1 font-medium text-N700"
                >
                  €{room.charges}/monthly charges
                </Typography>
              </Box>
              <Box sx={{ textAlign: 'right' }}>{getStatusChip(room.status, room.since)}</Box>
            </Box>
          ))
        ) : (
          <div className="flex h-full items-center justify-center">
            <EmptyState type="NoResults" size={125} />
          </div>
        )}
      </Box>

      {data?.data?.pagination && (
        <Box sx={{ mt: 2 }}>
          <TablePagination
            totalItems={data?.data?.pagination?.total}
            totalPages={data?.data?.pagination?.total_pages}
            currentPage={page}
            pageLimit={pageLimit}
            onPageChange={setPage}
            onPageLimitChange={(limit) => {
              setPageLimit(limit);
              setPage(1);
            }}
          />
        </Box>
      )}
    </Box>
  );
};

export const VacancyMaintenanceOverview: React.FC = () => {
  return (
    <VacancyMaintenanceFiltersProvider>
      <VacancyMaintenanceContent />
    </VacancyMaintenanceFiltersProvider>
  );
};
